import clsx from 'clsx';
import { useContextMenu } from 'components/common/context-menu-manager';
import { ContextMenuItemType } from 'data/types';
import React, { useCallback, useEffect, useRef, useState } from 'react';

const rotateCursorSvg = encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"><path fill="black" d="M12 4V1L8 5l4 4V6c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6H4c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z"/></svg>`
);
const rotateCursor = `url("data:image/svg+xml,${rotateCursorSvg}") 10 10, pointer`;

type LayoutImageOverlayProps = {
  imageUrl: string;
  opacity: number;
  scale: number;
  rotation: number;
  positionX: number;
  positionY: number;
  isSelected: boolean;
  onSelect: () => void;
  onDeselect: () => void;
  onTransformChange: (transform: {
    scale?: number;
    rotation?: number;
    positionX?: number;
    positionY?: number;
  }) => void;
  onTransformEnd: () => void;
  onImageLoad?: (dims: { naturalWidth: number; naturalHeight: number }) => void;
  isPanMode?: boolean;
  zoom: number;
};

type DragMode = 'none' | 'move' | 'scale' | 'rotate';
type ScaleCorner = 'nw' | 'ne' | 'sw' | 'se';

export default function LayoutImageOverlay({
  imageUrl,
  opacity,
  scale,
  rotation,
  positionX,
  positionY,
  isSelected,
  onSelect,
  onDeselect,
  onTransformChange,
  onTransformEnd,
  onImageLoad,
  isPanMode = false,
  zoom
}: LayoutImageOverlayProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const [dragMode, setDragMode] = useState<DragMode>('none');
  const [activeCorner, setActiveCorner] = useState<ScaleCorner | null>(null);
  const dragStartRef = useRef<{ x: number; y: number } | null>(null);
  const initialTransformRef = useRef<{
    scale: number;
    rotation: number;
    positionX: number;
    positionY: number;
  } | null>(null);
  const imageCenterRef = useRef<{ x: number; y: number } | null>(null);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (e.button === 2) return;

      if (!isSelected) {
        return;
      }

      e.stopPropagation();
      e.preventDefault();

      const target = e.target as HTMLElement;
      const corner = target.dataset.corner as ScaleCorner | undefined;
      const isRotateHandle = target.dataset.rotate === 'true';

      dragStartRef.current = { x: e.clientX, y: e.clientY };
      initialTransformRef.current = { scale, rotation, positionX, positionY };

      if (corner) {
        setDragMode('scale');
        setActiveCorner(corner);
      } else if (isRotateHandle) {
        setDragMode('rotate');
        if (containerRef.current) {
          const rect = containerRef.current.getBoundingClientRect();
          imageCenterRef.current = {
            x: rect.left + rect.width / 2,
            y: rect.top + rect.height / 2
          };
        }
      } else {
        setDragMode('move');
      }
    },
    [isSelected, scale, rotation, positionX, positionY]
  );

  const { openContextMenu } = useContextMenu();

  const handleContextMenu = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      const menuItems: ContextMenuItemType[] = [
        {
          label: 'Select Layout Underlay',
          onClick: onSelect,
          disabled: isSelected
        }
      ];

      openContextMenu(e.clientX, e.clientY, menuItems, 'layout-image');
    },
    [openContextMenu, onSelect, isSelected]
  );

  const handleDoubleClick = useCallback(
    (e: React.MouseEvent) => {
      if (!isSelected) {
        e.stopPropagation();
        onSelect();
      }
    },
    [isSelected, onSelect]
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (
        dragMode === 'none' ||
        !dragStartRef.current ||
        !initialTransformRef.current
      )
        return;

      const deltaX = e.clientX - dragStartRef.current.x;
      const deltaY = e.clientY - dragStartRef.current.y;

      if (dragMode === 'move') {
        onTransformChange({
          positionX: initialTransformRef.current.positionX + deltaX / zoom,
          positionY: initialTransformRef.current.positionY + deltaY / zoom
        });
      } else if (dragMode === 'scale' && activeCorner) {
        const initialDistance = Math.sqrt(
          (dragStartRef.current.x - (imageCenterRef.current?.x ?? 0)) ** 2 +
            (dragStartRef.current.y - (imageCenterRef.current?.y ?? 0)) ** 2
        );
        const currentDistance = Math.sqrt(
          (e.clientX - (imageCenterRef.current?.x ?? 0)) ** 2 +
            (e.clientY - (imageCenterRef.current?.y ?? 0)) ** 2
        );

        if (initialDistance > 0) {
          const scaleRatio = currentDistance / initialDistance;
          const newScale = Math.max(
            0.1,
            Math.min(5, initialTransformRef.current.scale * scaleRatio)
          );

          onTransformChange({ scale: newScale });
        }
      } else if (dragMode === 'rotate' && imageCenterRef.current) {
        const startAngle = Math.atan2(
          dragStartRef.current.y - imageCenterRef.current.y,
          dragStartRef.current.x - imageCenterRef.current.x
        );
        const currentAngle = Math.atan2(
          e.clientY - imageCenterRef.current.y,
          e.clientX - imageCenterRef.current.x
        );
        const angleDelta = ((currentAngle - startAngle) * 180) / Math.PI;
        let newRotation = initialTransformRef.current.rotation + angleDelta;

        if (e.shiftKey) {
          const snapAngle = 15;
          newRotation = Math.round(newRotation / snapAngle) * snapAngle;
        }

        onTransformChange({ rotation: newRotation });
      }
    },
    [dragMode, activeCorner, onTransformChange, zoom]
  );

  const handleMouseUp = useCallback(() => {
    if (dragMode !== 'none') {
      setDragMode('none');
      setActiveCorner(null);
      dragStartRef.current = null;
      initialTransformRef.current = null;
      imageCenterRef.current = null;
      onTransformEnd();
    }
  }, [dragMode, onTransformEnd]);

  useEffect(() => {
    if (!isSelected) return undefined;

    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        const target = e.target as HTMLElement;
        if (
          !target.closest('[data-grid-space]') &&
          !target.closest('[data-grid-object]') &&
          !target.closest('[data-layout-image]') &&
          !target.closest('[data-headlessui-state]') &&
          !target.closest('button')
        ) {
          onDeselect();
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isSelected, onDeselect]);

  useEffect(() => {
    if (dragMode === 'none') return undefined;

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragMode, handleMouseMove, handleMouseUp]);

  useEffect(() => {
    if (containerRef.current && isSelected) {
      const rect = containerRef.current.getBoundingClientRect();
      imageCenterRef.current = {
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2
      };
    }
  }, [isSelected, scale, rotation, positionX, positionY]);

  const handleCorners: ScaleCorner[] = ['nw', 'ne', 'sw', 'se'];

  const getCornerPosition = (corner: ScaleCorner) => {
    switch (corner) {
      case 'nw':
        return { top: -4, left: -4 };
      case 'ne':
        return { top: -4, right: -4 };
      case 'sw':
        return { bottom: -4, left: -4 };
      case 'se':
      default:
        return { bottom: -4, right: -4 };
    }
  };

  const getCornerCursor = (corner: ScaleCorner) => {
    switch (corner) {
      case 'nw':
      case 'se':
        return 'nwse-resize';
      case 'ne':
      case 'sw':
      default:
        return 'nesw-resize';
    }
  };

  const getRotateZonePosition = (corner: ScaleCorner) => {
    switch (corner) {
      case 'nw':
        return { top: -18, left: -18 };
      case 'ne':
        return { top: -18, right: -18 };
      case 'sw':
        return { bottom: -18, left: -18 };
      case 'se':
      default:
        return { bottom: -18, right: -18 };
    }
  };

  const getCursorClass = () => {
    if (isPanMode) return 'cursor-inherit';
    if (isSelected) return 'cursor-move';
    return 'cursor-default';
  };

  return (
    <div
      ref={containerRef}
      data-layout-image='true'
      className={clsx(
        'absolute z-0 flex items-center justify-center',
        getCursorClass(),
        dragMode !== 'none' && 'select-none'
      )}
      style={{
        left: 0,
        top: 0,
        transform: `translate(${positionX}px, ${positionY}px) rotate(${rotation}deg) scale(${scale})`,
        transformOrigin: 'center'
      }}
      onMouseDown={handleMouseDown}
      onContextMenu={handleContextMenu}
      onDoubleClick={handleDoubleClick}
    >
      <img
        ref={imageRef}
        src={imageUrl}
        alt='Layout underlay'
        className='max-w-none select-none'
        style={{ opacity: opacity / 100 }}
        draggable={false}
        onLoad={() => {
          if (onImageLoad && imageRef.current) {
            onImageLoad({
              naturalWidth: imageRef.current.naturalWidth,
              naturalHeight: imageRef.current.naturalHeight
            });
          }
        }}
      />

      {isSelected && (
        <>
          <div
            className='pointer-events-none absolute inset-0 border-blue-500'
            style={{ borderWidth: Math.max(2, 2 / scale) }}
          />

          {handleCorners.map((corner) => {
            const pos = getRotateZonePosition(corner);
            const handleSize = 20 / scale;
            const offset = -18 / scale;
            return (
              <div
                key={`rotate-${corner}`}
                data-rotate='true'
                className='absolute z-[5]'
                style={{
                  width: handleSize,
                  height: handleSize,
                  top: 'top' in pos ? offset : undefined,
                  bottom: 'bottom' in pos ? offset : undefined,
                  left: 'left' in pos ? offset : undefined,
                  right: 'right' in pos ? offset : undefined,
                  cursor: rotateCursor
                }}
                title='Rotate'
              />
            );
          })}

          {handleCorners.map((corner) => {
            const pos = getCornerPosition(corner);
            const handleSize = 8 / scale;
            const offset = -4 / scale;
            return (
              <div
                key={corner}
                data-corner={corner}
                className='absolute z-10 bg-blue-600'
                style={{
                  width: handleSize,
                  height: handleSize,
                  top: 'top' in pos ? offset : undefined,
                  bottom: 'bottom' in pos ? offset : undefined,
                  left: 'left' in pos ? offset : undefined,
                  right: 'right' in pos ? offset : undefined,
                  cursor: getCornerCursor(corner)
                }}
              />
            );
          })}
        </>
      )}
    </div>
  );
}
